import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Firestore, doc, docData } from '@angular/fire/firestore';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ChartConfiguration, ChartData } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { NGXLogger } from 'ngx-logger';
import { filter } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { ColoredIconComponent } from '../../components/icons/colored-icon/colored-icon.component';

@Component({
  selector: 'app-quiz-report',
  standalone: true,
  imports: [
    CommonModule,
    BtnBackComponent,
    BaseChartDirective,
    RouterLink,
    ColoredIconComponent,
  ],
  templateUrl: './quiz-report.component.html',
})
export class QuizReportComponent implements OnInit {
  private firestore = inject(Firestore);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private logger: NGXLogger = inject(NGXLogger);

  groupId = this.route.snapshot.paramMap.get('id');
  quizId = this.route.snapshot.paramMap.get('quizId');
  report = signal<any>(null);

  // Calculates total time spent by all participants in minutes/seconds
  totalQuizTime = computed(() => {
    const data = this.report();
    if (!data?.rankings) return '0s';

    // Sum up all averageTimeSeconds * answeredCount (if you tracked it)
    // or simply sum the rankings' time data if available.
    const totalSeconds = data.rankings.reduce((acc: number, r: any) => {
      // Note: We use average * count because your ranking stores the average
      // If you have a totalTime property in the ranking, use that instead.
      return acc + r.averageTimeSeconds * (data.troubleQuestions?.length || 0);
    }, 0);

    if (totalSeconds < 60) return `${Math.round(totalSeconds)}s`;
    const mins = Math.floor(totalSeconds / 60);
    const secs = Math.round(totalSeconds % 60);
    return `${mins}m ${secs}s`;
  });

  isLoading = signal<boolean>(true);

  constructor() {}

  ngOnInit(): void {
    if (this.groupId && this.quizId) {
      this.router.navigate(['/school', this.groupId, 'quizzes', this.quizId], { replaceUrl: true });
    } else {
      this.router.navigate(['/school', this.groupId, 'quizzes']);
    }
  }

  // --- CHART 1: PERFORMANCE BY BIBLE BOOK ---
  // Uses backend-calculated bibleStats, falls back to dynamic client-side mapping of troubleQuestions
  bookMasteryData = computed<ChartData<'bar'> | null>(() => {
    const data = this.report();
    if (!data) return null;

    if (data.bibleStats && data.bibleStats.length > 0) {
      const labels = data.bibleStats.map((item: any) => `${item.book} ${item.chapter}`);
      const successRates = data.bibleStats.map((item: any) => {
        const total = item.correct + item.wrong + item.unanswered;
        return total > 0 ? Math.round((item.correct / total) * 100) : 0;
      });
      return {
        labels,
        datasets: [
          {
            label: 'Success Rate (%)',
            data: successRates,
            backgroundColor: '#4f46e5', // Indigo
            borderRadius: 5,
          },
        ],
      };
    }

    if (!data.troubleQuestions) return null;

    const stats: Record<string, { correct: number; total: number }> = {};
    data.troubleQuestions.forEach((q: any) => {
      const book = q.book || 'Other';
      if (!stats[book]) stats[book] = { correct: 0, total: 0 };
      stats[book].total += q.totalResponses;
      stats[book].correct += q.totalResponses - q.wrongCount;
    });

    return {
      labels: Object.keys(stats),
      datasets: [
        {
          label: 'Success Rate (%)',
          data: Object.values(stats).map((s) => (s.correct / s.total) * 100),
          backgroundColor: '#4f46e5', // Indigo
          borderRadius: 5,
        },
      ],
    };
  });

  // --- CHART 2: STUDENT PERFORMANCE SCATTER ---
  // Shows who is Fast & Accurate vs. Slow & Struggling
  scatterData = computed<ChartData<'scatter'> | null>(() => {
    const data = this.report();
    if (!data?.rankings) return null;

    return {
      datasets: [
        {
          label: 'Students',
          data: data.rankings.map((r: any) => ({
            x: r.averageTimeSeconds,
            y: r.userScore ?? r.totalScore,
            name: r.name,
          })),
          backgroundColor: '#ec4899', // Pink
          pointRadius: 8,
        },
      ],
    };
  });

  // --- CHART 3: QUESTION DIFFICULTY BUBBLE ---
  difficultyBubbleData = computed<ChartData<'bubble'> | null>(() => {
    const data = this.report();
    if (!data?.questionStats || data.questionStats.length === 0) return null;

    return {
      datasets: [
        {
          label: 'Question Performance',
          data: data.questionStats.map((q: any, index: number) => ({
            x: index + 1,
            y: Math.round(q.difficulty * 100),
            r: Math.min(25, Math.max(6, (q.wrongCount + q.unansweredCount) * 2)),
            label: q.questionText,
            ref: `${q.book} ${q.chapter}:${q.verse}`,
            correct: q.correctCount,
            wrong: q.wrongCount,
            unanswered: q.unansweredCount
          })),
          backgroundColor: 'rgba(239, 68, 68, 0.6)',
          borderColor: '#ef4444',
          borderWidth: 1,
        }
      ]
    };
  });

  // Chart Options
  barOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: { min: 0, max: 100, title: { display: true, text: 'Mastery %' } },
    },
  };

  scatterOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: { title: { display: true, text: 'Avg Time (s)' } },
      y: { title: { display: true, text: 'Score' } },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx: any) => `${ctx.raw.name}: Score ${ctx.raw.y}`,
        },
      },
    },
  };

  bubbleOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: { display: true, text: 'Question Number' },
        ticks: { stepSize: 1 }
      },
      y: {
        min: 0,
        max: 100,
        title: { display: true, text: 'Difficulty (% Incorrect/Unanswered)' }
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const raw = ctx.raw;
            return [
              `Q${raw.x}: ${raw.label}`,
              `Ref: ${raw.ref}`,
              `Difficulty: ${raw.y}%`,
              `Correct: ${raw.correct}`,
              `Wrong: ${raw.wrong}`,
              `Unanswered: ${raw.unanswered}`
            ];
          }
        }
      }
    }
  };

  getStudentPhotoUrl(userId: string): string | null {
    const ranking = this.report()?.rankings?.find((r: any) => r.uid === userId);
    return ranking?.photoURL || ranking?.photoUrl || null;
  }
}
